import React, { useState, useEffect } from 'react';
import BackOfficeLayout from '../../components/backoffice/BackOfficeLayout';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Clock, CheckCircle, XCircle } from 'lucide-react';
import { toast } from '../../lib/safeToast';

const STATUS_LABELS = {
  in_review: 'In Review',
  info_requested: 'Info Requested',
  co_recommended_approve: 'Compliance Approved',
  co_recommended_reject: 'Compliance Rejected',
  pending: 'Pending',
};

const STATUS_BADGE = {
  in_review: 'bg-blue-50 text-blue-700 border-blue-200',
  info_requested: 'bg-amber-50 text-amber-700 border-amber-200',
  co_recommended_approve: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  co_recommended_reject: 'bg-red-50 text-red-700 border-red-200',
  pending: 'bg-slate-50 text-slate-600 border-slate-200',
};

const BackOfficeMerchantPending = () => {
  const { api } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const results = await Promise.allSettled([
          api.get('/backoffice/merchants?per_page=50&kyc_status=in_review'),
          api.get('/backoffice/merchants?per_page=50&kyc_status=co_recommended_approve'),
          api.get('/backoffice/merchants?per_page=50&kyc_status=co_recommended_reject'),
          api.get('/backoffice/merchants?per_page=50&kyc_status=info_requested'),
        ]);
        const all = [];
        for (const r of results) {
          if (r.status === 'fulfilled') all.push(...(r.value.data?.data || []));
        }
        const seen = new Set();
        const deduped = all.filter(m => { if (seen.has(m.id)) return false; seen.add(m.id); return true; });
        deduped.sort((a, b) => {
          const order = { co_recommended_approve: 0, co_recommended_reject: 1, in_review: 2, info_requested: 3 };
          return (order[a.kyc_status] ?? 9) - (order[b.kyc_status] ?? 9);
        });
        setData(deduped);
      } catch {
        toast.error('Failed to load pending queue');
        setData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [api]);

  return (
    <BackOfficeLayout>
      <div className="space-y-6">
        <div className="pb-4">
          <h1 className="text-2xl font-bold text-[#073B2E] flex items-center gap-2">
            <Clock className="w-7 h-7 text-[#0B6445]" />
            Pending Approval Queue
          </h1>
          <p className="text-[#0E1B16]/70 mt-2">Merchants awaiting KYC review or Super Admin final decision.</p>
        </div>
        <Card className="border-[#DCE4E0] shadow-sm">
          <CardContent className="pt-6">
            {loading ? (
              <div className="flex justify-center py-12"><div className="spinner w-8 h-8" /></div>
            ) : data.length === 0 ? (
              <p className="text-center py-12 text-[#0E1B16]/60">No applications in the pending queue.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#DCE4E0]">
                      <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">Name</th>
                      <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">Email</th>
                      <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">Status</th>
                      <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((row) => (
                      <tr key={row.id} className="border-b border-[#DCE4E0]/80 hover:bg-[#F8FAFC]">
                        <td className="py-3 px-2">
                          <div className="font-medium">{row.name || '—'}</div>
                          {row.company_name && <div className="text-xs text-slate-400">{row.company_name}</div>}
                        </td>
                        <td className="py-3 px-2">{row.email}</td>
                        <td className="py-3 px-2">
                          <Badge variant="outline" className={STATUS_BADGE[row.kyc_status] || STATUS_BADGE.pending}>
                            {row.kyc_status === 'co_recommended_approve' && <CheckCircle className="w-3 h-3 mr-1" />}
                            {row.kyc_status === 'co_recommended_reject' && <XCircle className="w-3 h-3 mr-1" />}
                            {STATUS_LABELS[row.kyc_status] || row.kyc_status}
                          </Badge>
                        </td>
                        <td className="py-3 px-2">{row.created_at ? new Date(row.created_at).toLocaleDateString() : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </BackOfficeLayout>
  );
};

export default BackOfficeMerchantPending;
